import { useState } from 'react';
import {
    Box, Typography, Button, Stack, Alert, CircularProgress, IconButton, Tooltip, Chip,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../api/client';


/**
 * Globāli ignorēto ārējo (Unverified) vietu pārvaldība. Ieraksti glabājas
 * userdata.external_ignored ar sentinel user_id='GLOBAL', un serveris tos iemiksē
 * katra lietotāja /api/v1/external-ignored/my atbildē - karte tos filtrē visiem.
 *
 * Server endpoints (AdminExternalIgnoredController.cs):
 *   POST /api/v1/admin/external-ignored/list
 *   POST /api/v1/admin/external-ignored/add
 *   POST /api/v1/admin/external-ignored/remove
 */
type IgnoredItem = {
    source: string;
    externalId: string;
    createdAt: string;
    createdBy: string;
};

type ListResponse = {
    isSuccess: boolean;
    error: string | null;
    items: IgnoredItem[];
};

const SOURCES = ['GOOGLE', 'OSM', 'REFUGEE', 'FOURSQUARE'];


export function ExternalIgnoredPage() {
    const queryClient = useQueryClient();
    const [addOpen, setAddOpen] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState<IgnoredItem | null>(null);

    const { data, isLoading, error, refetch, isFetching } = useQuery({
        queryKey: ['admin-external-ignored'],
        queryFn: async () => {
            const resp = await api.post<ListResponse>('/api/v1/admin/external-ignored/list', {});
            return resp.data;
        },
    });

    const addMutation = useMutation({
        mutationFn: async (args: { source: string; externalId: string }) => {
            const resp = await api.post<{ isSuccess: boolean; error: string | null }>(
                '/api/v1/admin/external-ignored/add',
                args,
            );
            if (!resp.data.isSuccess) throw new Error(resp.data.error || 'Pievienošana neizdevās.');
            return resp.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin-external-ignored'] });
            setAddOpen(false);
        },
    });

    const removeMutation = useMutation({
        mutationFn: async (args: { source: string; externalId: string }) => {
            const resp = await api.post<{ isSuccess: boolean; error: string | null }>(
                '/api/v1/admin/external-ignored/remove',
                args,
            );
            if (!resp.data.isSuccess) throw new Error(resp.data.error || 'Noņemšana neizdevās.');
            return resp.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin-external-ignored'] });
            setConfirmDelete(null);
        },
    });

    const items = data?.items ?? [];

    return (
        <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                <Box>
                    <Typography variant="h4">Ignorētās ārējās vietas</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Globāli slēptās Unverified vietas — kartē tās neredz neviens lietotājs.
                        Der bojātiem / dublētiem / neeksistējošiem ārējo avotu ierakstiem.
                    </Typography>
                </Box>
                <Stack direction="row" spacing={2} alignItems="center">
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setAddOpen(true)}
                    >
                        Pievienot
                    </Button>
                    <Tooltip title="Atjaunot">
                        <span>
                            <IconButton onClick={() => refetch()} disabled={isFetching}>
                                <RefreshIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Stack>
            </Stack>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    Neizdevās ielādēt: {String((error as Error).message)}
                </Alert>
            )}

            {removeMutation.error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    Noņemšana neizdevās: {(removeMutation.error as Error).message}
                </Alert>
            )}

            {isLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            )}

            {!isLoading && items.length === 0 && (
                <Alert severity="info">
                    Globāli ignorētu vietu nav.
                </Alert>
            )}

            {!isLoading && items.length > 0 && (
                <TableContainer component={Paper}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Avots</TableCell>
                                <TableCell>External ID</TableCell>
                                <TableCell>Pievienots</TableCell>
                                <TableCell>Pievienoja</TableCell>
                                <TableCell align="right">Darbības</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {items.map((it) => (
                                <TableRow key={`${it.source}:${it.externalId}`}>
                                    <TableCell>
                                        <Chip label={it.source} size="small" variant="outlined" />
                                    </TableCell>
                                    <TableCell sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                        {it.externalId}
                                    </TableCell>
                                    <TableCell>
                                        {new Date(it.createdAt).toLocaleString('lv-LV')}
                                    </TableCell>
                                    <TableCell>
                                        <Tooltip title={it.createdBy}>
                                            <Typography
                                                variant="body2"
                                                sx={{ fontFamily: 'monospace' }}
                                                noWrap
                                            >
                                                {it.createdBy.slice(0, 8)}…
                                            </Typography>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip title="Noņemt no ignorētajām (vieta atkal parādīsies kartē)">
                                            <IconButton size="small" onClick={() => setConfirmDelete(it)}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {addOpen && (
                <AddIgnoredDialog
                    onClose={() => setAddOpen(false)}
                    onSave={(args) => addMutation.mutate(args)}
                    busy={addMutation.isPending}
                    errorText={addMutation.error ? (addMutation.error as Error).message : null}
                />
            )}

            {confirmDelete && (
                <Dialog open onClose={() => setConfirmDelete(null)}>
                    <DialogTitle>Noņemt no ignorētajām?</DialogTitle>
                    <DialogContent>
                        <Typography>
                            Vieta <b>{confirmDelete.source}</b> / <code>{confirmDelete.externalId}</code> atkal
                            kļūs redzama kartē visiem lietotājiem.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setConfirmDelete(null)}>Atcelt</Button>
                        <Button
                            color="error"
                            disabled={removeMutation.isPending}
                            onClick={() => removeMutation.mutate({
                                source: confirmDelete.source,
                                externalId: confirmDelete.externalId,
                            })}
                        >
                            Noņemt
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </Box>
    );
}


/**
 * Jauna globāla ignore ieraksta dialogs. External ID formāts atkarīgs no avota
 * (Google place_id, OSM node/way id, Refugee Restrooms id, Foursquare fsq_id).
 */
function AddIgnoredDialog({
    onClose, onSave, busy, errorText,
}: {
    onClose: () => void;
    onSave: (args: { source: string; externalId: string }) => void;
    busy: boolean;
    errorText: string | null;
}) {
    const [source, setSource] = useState('GOOGLE');
    const [externalId, setExternalId] = useState('');

    return (
        <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Ignorēt ārējo vietu</DialogTitle>
            <DialogContent>
                <Stack spacing={2} mt={1}>
                    {errorText && <Alert severity="error">{errorText}</Alert>}
                    <TextField
                        label="Avots"
                        select
                        value={source}
                        onChange={(e) => setSource(e.target.value)}
                        fullWidth
                    >
                        {SOURCES.map((s) => (
                            <MenuItem key={s} value={s}>{s}</MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        label="External ID"
                        value={externalId}
                        onChange={(e) => setExternalId(e.target.value)}
                        autoFocus
                        fullWidth
                        helperText="Vietas ID avota sistēmā — Google place_id, OSM node/way ID, Refugee id vai Foursquare fsq_id. Redzams vietas detaļu skatā un ārējo atsauksmju moderācijā."
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Atcelt</Button>
                <Button
                    variant="contained"
                    onClick={() => onSave({ source, externalId: externalId.trim() })}
                    disabled={busy || !externalId.trim()}
                >
                    Saglabāt
                </Button>
            </DialogActions>
        </Dialog>
    );
}
